import { useCookies } from "react-cookie";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import Swal from "sweetalert2";
import { config } from "../../data/config";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

export default function AuthPage() {
  const { t } = useTranslation();
  const [cookies, setCookie] = useCookies(["token", "rule"]);
  const [email, setEmail] = useState("");
  const [password, setPasssword] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18next.language || 'fa');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const availableLanguages = [
    { code: "fa", name: "فارسی", flag: "🇮🇷" },
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "ar", name: "العربية", flag: "🇸🇦" }
  ];

  const changeLanguage = (lng: string) => {
    if (!['fa', 'en', 'ar'].includes(lng)) {
      return;
    }
    
    i18next.changeLanguage(lng);
    setCurrentLanguage(lng);
    setShowLanguageMenu(false);
    
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('i18nextLng', lng);
      } catch (e) {
        console.warn('Error saving to localStorage:', e);
      }
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('i18nextLng');
        if (saved && ['fa', 'en', 'ar'].includes(saved)) {
          if (i18next.language !== saved) {
            i18next.changeLanguage(saved);
          }
          setCurrentLanguage(saved);
        } else {
          const currentLang = i18next.language || 'fa';
          localStorage.setItem('i18nextLng', currentLang);
          setCurrentLanguage(currentLang);
        }
      } catch (e) {
        console.warn('Error reading from localStorage:', e);
        setCurrentLanguage(i18next.language || 'fa');
      }
    }
  }, []);

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng);
      if (typeof window !== 'undefined') {
        localStorage.setItem('i18nextLng', lng);
      }
    };
    
    i18next.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18next.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const isRTL = currentLanguage === 'fa' || currentLanguage === 'ar';

  const submit = async () => {
    if (!email || !password) {
      Swal.fire(t("Please fill in all fields"));
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${config.url}/v1/auth/login`, {
        email,
        password,
      });

      setCookie("token", res.data.token, { path: "/" });
      setCookie("rule", res.data.rule, { path: "/" });
      window.location.href = "/dashboard";
    } catch (error: any) {
      Swal.fire(error.response?.data?.message || t("Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen bg-gradient-to-br from-red-400 via-red-500 to-red-700 overflow-hidden" dir={isRTL ? "rtl" : "ltr"}>
      {/* Language Selector */}
      <div className={`absolute top-4 z-20 ${isRTL ? 'left-4' : 'right-4'}`}>
        <div className="relative">
          <button
            onClick={() => setShowLanguageMenu(!showLanguageMenu)}
            className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-md hover:bg-white transition-colors"
          >
            <span className="text-lg">
              {availableLanguages.find(lang => lang.code === currentLanguage)?.flag}
            </span>
            <span className="text-sm font-medium text-gray-700">
              {availableLanguages.find(lang => lang.code === currentLanguage)?.name}
            </span>
            <svg
              className={`w-4 h-4 text-gray-600 transition-transform ${showLanguageMenu ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showLanguageMenu && (
            <div className={`absolute top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden min-w-[160px] z-30 ${
              isRTL ? 'left-0' : 'right-0'
            }`}>
              {availableLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors ${
                    currentLanguage === lang.code ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span>{lang.name}</span>
                  {currentLanguage === lang.code && (
                    <svg className={`w-4 h-4 ${isRTL ? 'mr-auto' : 'ml-auto'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Close language menu when clicking outside */}
      {showLanguageMenu && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowLanguageMenu(false)}
        />
      )}

      {/* Floating circles */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.3, type: "spring" }}
        className="absolute w-40 h-40 bg-white/20 rounded-full top-[10%] left-[10%] blur-xl"
      />
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.5, type: "spring" }}
        className="absolute w-32 h-32 bg-white/20 rounded-full bottom-[15%] right-[12%] blur-lg"
      />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="z-10 w-[90%] sm:w-[600px] bg-white/90 backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="grid sm:grid-cols-2">
          {/* Left Side */}
          <div
            className="hidden sm:block bg-cover bg-center"
            style={{ backgroundImage: 'url(/intro.jpg)' }}
          ></div>

          {/* Right Side */}
          <div className="p-8 sm:p-10 flex flex-col justify-center">
            <h1 className="text-sm font-bold text-blue-600 text-center mb-2">
              {t("Welcome")}
            </h1>
           
            <div className="space-y-5">
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1">
                  {t("Username")}
                </label>
                <input
                  onChange={(e) => setEmail(e.target.value)}
                  type="text"
                  value={email}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition outline-none"
                  placeholder={t("Enter your email")}
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1">
                  {t("Password")}
                </label>
                <input
                  onChange={(e) => setPasssword(e.target.value)}
                  type="password"
                  value={password}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition outline-none"
                  placeholder="••••••••••"
                />
              </div>

              <motion.button
                whileTap={{ scale: 0.95 }}
                disabled={loading}
                onClick={submit}
                className={`w-full py-2 rounded-lg text-white font-semibold shadow-md transition-all ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-500 hover:bg-blue-600 active:bg-blue-700"
                }`}
              >
                {loading ? t("Signing in...") : t("Sign In")}
              </motion.button>
            </div>

            <div className="mt-8 text-center text-gray-500 text-[10px]">
              {/* <p>
                {t("Programmed by")}: <strong>Esmaiel Pouryosef</strong>
              </p> */}
              <p>
                {t("Version")}: <strong>2.1.0</strong>
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
